import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Layout
import Layout from "@/components/Layout";

// Public Pages
import HomePage from "@/pages/HomePage";
import WhyWeExistPage from "@/pages/WhyWeExistPage";
import WhoWeHelpPage from "@/pages/WhoWeHelpPage";
import WhoThisIsNotForPage from "@/pages/WhoThisIsNotForPage";
import HowWeHelpPage from "@/pages/HowWeHelpPage";
import WhatWeDoPage from "@/pages/WhatWeDoPage";
import WhatWeBuildPage from "@/pages/WhatWeBuildPage";
import ProcessPage from "@/pages/ProcessPage";
import PricingPage from "@/pages/PricingPage";
import EngagementTypesPage from "@/pages/EngagementTypesPage";
import TermsPage from "@/pages/TermsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AboutPage from "@/pages/AboutPage";
import FounderStoryPage from "@/pages/FounderStoryPage";
import ContactPage from "@/pages/ContactPage";
import DashboardPage from "@/pages/DashboardPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import InvestorsPage from "@/pages/InvestorsPage";

// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import LeadsPage from "@/pages/admin/LeadsPage";
import BlogListPage from "@/pages/admin/BlogListPage";
import BlogEditorPage from "@/pages/admin/BlogEditorPage";
import TeamPage from "@/pages/admin/TeamPage";
import SettingsPage from "@/pages/admin/SettingsPage";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/why-we-exist" element={<WhyWeExistPage />} />
        <Route path="/who-we-help" element={<WhoWeHelpPage />} />
        <Route path="/not-for-everyone" element={<WhoThisIsNotForPage />} />
        <Route path="/how-we-help" element={<HowWeHelpPage />} />
        <Route path="/what-we-do" element={<WhatWeDoPage />} />
        <Route path="/what-we-build" element={<WhatWeBuildPage />} />
        <Route path="/process" element={<ProcessPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/engagements" element={<EngagementTypesPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogPostPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/founder" element={<FounderStoryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:id" element={<LeadsPage />} />
        <Route path="blog" element={<BlogListPage />} />
        <Route path="blog/new" element={<BlogEditorPage />} />
        <Route path="blog/:id" element={<BlogEditorPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
